import { motion } from "framer-motion";
import { BookOpen, Mic2, Newspaper, GraduationCap, ArrowRight } from "lucide-react";
import { publications } from "@/data";
import { PublicationCard } from "@/components/Cards";
import { IMAGES } from "@/assets/images";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Publications() {
  const categories = [
    { id: "ksiazka", label: "Książki", icon: BookOpen },
    { id: "artykul", label: "Artykuły", icon: Newspaper },
    { id: "konferencja", label: "Konferencje", icon: GraduationCap },
    { id: "media", label: "Media", icon: Mic2 },
  ];

  return (
    <div className="min-h-screen pb-24">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={IMAGES.MODERN_OFFICE_4}
            alt="Academic library"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={springPresets.gentle}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-serif mb-6 leading-tight italic">
              Publikacje i Działalność Naukowa
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/80 leading-relaxed">
              Dzielenie się wiedzą to dla mnie fundament rzetelnej praktyki. Łączę perspektywę akademicką z wieloletnim doświadczeniem w gabinecie i biznesie.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories / Statistics Overview */}
      <section className="container mx-auto px-4 -mt-12 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.snappy, delay: 0.1 }}
              className="bg-card p-6 rounded-xl border border-border shadow-sm flex flex-col items-center text-center"
            >
              <div className="p-3 rounded-full bg-secondary mb-3 text-primary">
                <cat.icon size={24} />
              </div>
              <span className="font-medium text-foreground">{cat.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Publications Grid */}
      <section className="container mx-auto px-4 mt-24">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
              Dorobek Autorski
            </h2>
            <p className="text-muted-foreground">
              Zestawienie wybranych publikacji książkowych, artykułów naukowych oraz wystąpień na konferencjach branżowych i interdyscyplinarnych.
            </p>
          </div>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {publications.map((pub) => (
            <motion.div key={pub.id} variants={staggerItem}>
              <PublicationCard publication={pub} />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Featured Book / Call to Action */}
      <section className="container mx-auto px-4 mt-32">
        <div className="bg-muted rounded-3xl overflow-hidden flex flex-col lg:flex-row border border-border">
          <div className="lg:w-1/2 p-12 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 text-accent font-semibold mb-6 tracking-wider uppercase text-sm">
              <BookOpen size={18} />
              <span>Polecana Książka</span>
            </div>
            <h3 className="text-3xl md:text-5xl font-serif text-foreground mb-6">
              Psychologia Zmiany: Między Gabinetem a Biznesem
            </h3>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Moja najnowsza publikacja to przewodnik po procesach psychologicznych, które towarzyszą nam w kluczowych momentach życia i kariery. Dowiedz się, jak budować odporność psychiczną w świecie pełnym wyzwań.
            </p>
            <button className="group flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full font-medium hover:bg-primary/90 transition-all w-fit">
              Zamów egzemplarz
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <div className="lg:w-1/2 relative min-h-[400px]">
            <img
              src={IMAGES.MODERN_OFFICE_8}
              alt="Książka Racheli Nowak"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-muted/50 via-transparent to-transparent" />
          </div>
        </div>
      </section>



      {/* Quote Section */}
      <section className="container mx-auto px-4 mt-32 max-w-4xl">
        <div className="border-t border-b border-border py-16 text-center italic">
          <p className="text-2xl md:text-3xl font-serif text-foreground leading-relaxed">
            &bdquo;Nauka i praktyka nie są rozłącznymi światami. Moim celem jest przekładanie skomplikowanych teorii psychologicznych na język konkretnych działań, które realnie zmieniają jakość życia.&rdquo;
          </p>
          <p className="mt-6 font-medium text-muted-foreground uppercase tracking-widest">
            &mdash; Rachela Nowak
          </p>
        </div>
      </section>
    </div>
  );
}
